/**
 * Botón-icono pequeño sin label visible. Casos: back, theme toggle,
 * hamburger, close, dismiss, FAB Revisa.
 *
 * No es para acciones primarias con texto — eso es <Button>. Si necesitás
 * un botón con icono + texto, usá <Button iconLeft={...}>texto</Button>.
 *
 * Reglas duras:
 *   - ariaLabel SIEMPRE obligatorio.
 *   - Hit area mínimo 44px (mínimo táctil) aunque el icon sea menor.
 *   - No anidar dentro de <Button>.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon   Componente lucide-react ya con su
 *                                        tamaño y stroke-width 1.6.
 * @param {string} props.ariaLabel       Texto para lectores de pantalla.
 *                                        Sin esto el componente lanza warn
 *                                        en dev.
 * @param {'default'|'muted'|'inverse'} [props.variant='default']
 * @param {'sm'|'md'} [props.size='md']  Hit area: sm = 40×40, md = 44×44.
 * @param {boolean} [props.disabled=false]
 * @param {(e: React.MouseEvent) => void} [props.onClick]
 * @param {'button'|'submit'|'reset'} [props.type='button']
 * @param {string} [props.className='']  Solo posicionamiento (ej. absolute
 *                                        top-3 right-3). No para color.
 */
export default function IconButton({
  icon,
  ariaLabel,
  variant = 'default',
  size = 'md',
  disabled = false,
  onClick,
  type = 'button',
  className = '',
}) {
  if (process.env.NODE_ENV !== 'production' && !ariaLabel) {
    // eslint-disable-next-line no-console
    console.warn('[IconButton] ariaLabel es obligatorio. El botón es invisible para lectores de pantalla sin él.');
  }

  const variantClass = VARIANTS[variant] || VARIANTS.default;
  const sizeClass = size === 'sm' ? SIZE_SM : SIZE_MD;
  const focusRing =
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper';

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={[
        'inline-flex items-center justify-center rounded-full',
        'transition-all duration-150',
        sizeClass,
        variantClass,
        focusRing,
        disabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-[0.95] cursor-pointer',
        className,
      ].filter(Boolean).join(' ')}
    >
      {icon}
    </button>
  );
}

const VARIANTS = {
  default: 'text-ink hover:bg-surface-2',
  muted:   'text-ink-muted hover:text-ink hover:bg-surface-2',
  // bg-ui-inverse está mapeado a var() directo (sin rgb-channel), así que
  // /alpha no aplica. Usamos hover:opacity-90 sobre el botón completo —
  // suficiente para señalar interactividad sin romper el contraste.
  inverse: 'bg-ui-inverse text-paper hover:opacity-90',
};

// Hit area 40×40 (sm) y 44×44 (md, mínimo táctil), en PÍXELES y no en rem:
// el html base mide 14.4px (index.css), así que `w-11` daba 39,6 px y `w-10`
// 36 px, no los 44 y 40 que este comentario prometía desde siempre (medido en
// Chromium el 2026-10-14 al crear la skill diseno-mipae). El encabezado usa sm
// a propósito: a 44 px sus cuatro botones se salen de un teléfono de 320 px
// con letra "Muy grande", a 40 caben en todos los casos (medido igual). El
// icon size lo decide el caller vía props del componente lucide; el botón
// solo asegura el área.
const SIZE_SM = 'w-[40px] h-[40px]';
const SIZE_MD = 'w-[44px] h-[44px]';
